import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Subject } from 'rxjs';

import { AnalyticsBrowser, Context, SondermindAnalytics } from '@sondermind/analytics';
import { ConfigurationService, ISegmentAppConfig } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { isBotUserAgent, snakeCaseTransformer } from '@sondermind/utilities/tools';

export interface SegmentTrackEmission {
  event: string;
  properties: Record<string, unknown>;
  timestamp: number;
}

// Intentionally widened to allow compatibility between types defined in @sondermind/analytics
// and types defined in sonder-client. To be deprecated until once codebase is migrated - tracked
// in MER-4501
export type SegmentSchema = object;

@Injectable({
  providedIn: 'root'
})
export class SegmentService<TSchema extends SegmentSchema> {
  analytics: AnalyticsBrowser | undefined;

  private trackEventSubject = new Subject<SegmentTrackEmission>();
  readonly trackEvent$ = this.trackEventSubject.asObservable();

  // Store the explicitly identified user ID (set when identifyUser is called)
  private identifiedUserId: string | null = null;

  constructor(
    private configurationService: ConfigurationService<ISegmentAppConfig>,
    private logger: Logger,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
  ) {
  }

  async init(): Promise<boolean> {
    if (isPlatformServer(this.platformId)) {
      this.logger.warn('Aborting Segment Initialization because Segment not setup to run on server.');
      return false;
    }
    if (!this.configurationService.env.app?.segmentWriteKey) {
      this.logger.warn('Aborting Segment Initialization because of missing write key.');
      return false;
    }
    if (this.configurationService.env.app?.segmentDisabled) {
      this.logger.warn('Aborting Segment Initialization because it\'s been disabled.');
      return false;
    }
    if (isBotUserAgent()) {
      this.logger.info('Aborting Segment initialization for bot traffic.');
      return false;
    }
    try {
      if (!this.analytics) {
        const enablePixels = !!this.configurationService.env.app?.enableSegmentPixelDest;
        const cdnURL = 'https://segment.sondermind.com';
        const apiHost = 'segment-api.sondermind.com/v1';
        const integrations = {
          All: true,
          'Facebook Pixel': enablePixels,
          'TikTok Pixel': enablePixels,
          'Google AdWords New': enablePixels,
          'Bing Ads': enablePixels,
          'Segment.io': { apiHost }
        };
        // Load runs CDN / network work outside the Angular zone so it does not block isStable.
        const analyticsBrowser = this.ngZone.runOutsideAngular(() => SondermindAnalytics.load(
          {
            writeKey: this.configurationService.env.app?.segmentWriteKey ?? '',
            cdnURL,
            integrations,
            consent: { enabled: !!this.configurationService.env.app?.enableCookieConsent },
          }
        ));
        await analyticsBrowser;
        this.analytics = analyticsBrowser;
      }
    } catch (error) {
      this.logger.warn('Failed to initialize Segment analytics:', error as Error);
      this.analytics = undefined;
      return false;
    }

    return true;
  }

  async identifyUser(platformAccountUuid: string): Promise<Context | void> {
    const initializationSuccessful = await this.init();
    if (!initializationSuccessful) {
      return undefined;
    }

    this.identifiedUserId = platformAccountUuid;
    return this.analytics?.identify(platformAccountUuid);
  }

  async anonymousId(): Promise<string | undefined | null> {
    const initializationSuccessful = await this.init();

    if (!initializationSuccessful) {
      return undefined;
    }

    const analyticsUser = await this.analytics?.user();
    return analyticsUser?.anonymousId();
  }

  async userId(): Promise<string | undefined | null> {
    const initializationSuccessful = await this.init();

    if (!initializationSuccessful) {
      return undefined;
    }

    // Return the explicitly identified user ID if it was set via identifyUser()
    // This ensures we only return a user ID when the app has explicitly identified the user,
    // not from stale localStorage across different apps on the same device
    return this.identifiedUserId;
  }

  /**
   * This registers an event with segment, posting tracking data along with the name of the action.
   *
   * One important note is that segment collects anonymousId implicitly. You do not need to include that as
   * part of your changesets.
   */
  async track<T extends keyof TSchema>(event: T, data: TSchema[T]): Promise<void> {
    if (!this.analytics) {
      // if initialization fails (like when no segmentWriteKey is provided) and returns false
      // we do nothing with the tracking event/data
      if (!await this.init()) {
        return;
      }
    }

    // Automatically make the data snake case
    const snakeCaseData = snakeCaseTransformer(data);
    // if analytics exists (implying init succeeded already at any point)
    // we track the event and post the data.
    await this.analytics?.track(event.toString(), snakeCaseData as Record<string, unknown>).catch(
      (error: Error) => {
        this.logger.error(
          `Segment: An error occurred when trying to track ${String(event)}: ${error?.message ?? 'unknown error'}`
        );
      }
    );

    this.trackEventSubject.next({
      event: event.toString(),
      properties: snakeCaseData as Record<string, unknown>,
      timestamp: Date.now(),
    });
  }

  /**
   * Emits Segment's built-in Page spec for the current page. Called SOURCE-ONLY (no name/category
   * args) per MER-4963's Derived-Properties decision; url/path/referrer/title/search are
   * auto-collected by Segment. Mirrors track()'s init-guard pattern.
   */
  async page(): Promise<void> {
    if (!this.analytics) {
      // if initialization fails (like when no segmentWriteKey is provided) and returns false
      // we do nothing and treat page() as a safe no-op
      if (!await this.init()) {
        return;
      }
    }

    // SOURCE-ONLY: no name/category args (MER-4963 Derived-Properties decision); url/path/
    // referrer/title/search are auto-collected by Segment's built-in Page spec.
    await this.analytics?.page().catch(
      (error: Error) => {
        this.logger.error(
          `Segment: An error occurred when trying to emit page: ${error?.message ?? 'unknown error'}`
        );
      }
    );
  }
}
